import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { SignupStepOneComponent } from './steps/step-one.component';
import { SignupStepTwoComponent } from './steps/step-two.component';
import { SignupStepThreeComponent } from './steps/step-three.component';
import { ApiRequestService } from '@app/_services/api-Services/api-request.service';
import { HttpResponse } from '@angular/common/http';

// Custom validator for password matching
function passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
  const password = control.get('password');
  const confirmPassword = control.get('confirmPassword');
  
  if (!password || !confirmPassword) {
    return null;
  }
  
  return password.value === confirmPassword.value ? null : { passwordMismatch: true };
}

@Component({
  selector: 'app-sign-up',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SignupStepOneComponent, SignupStepTwoComponent, SignupStepThreeComponent],
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss']
})
export class SignUpComponent implements OnInit {
  signupForm!: FormGroup;
  currentStep: number = 1;
  isLoading: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private apiRequestService: ApiRequestService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    this.signupForm = this.fb.group({
      // Step 1 fields
      fullName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required, Validators.pattern(/^[0-9]{10,15}$/)]],
      
      // Step 2 fields
      companyName: ['', [Validators.required, Validators.minLength(2)]],
      industry: ['', [Validators.required]],
      numberOfEmployees: ['', [Validators.required, Validators.min(1)]],
      
      // Step 3 fields
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]],
      termsAccepted: [false, [Validators.requiredTrue]]
    }, { validators: passwordMatchValidator });
  }

  nextStep(): void {
    if (this.currentStep < 3) {
      this.currentStep++;
      this.errorMessage = '';
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.errorMessage = '';
    }
  }

  onSubmit(): void {
    this.errorMessage = '';
    this.successMessage = '';

    if (this.signupForm.invalid) {
      this.errorMessage = 'Please fill in all required fields correctly.';
      return;
    }

    this.isLoading = true;
    this.performSignup();
  }

  private performSignup(): void {
    const formData = this.signupForm.value;

    // Prepare request body according to API specification
    const requestBody = {
      companyName: formData.companyName,
      companyField: formData.industry,
      numberOfEmployees: formData.numberOfEmployees,
      contactPersonName: formData.fullName,
      contactPersonMobile: formData.phone,
      contactPersonEmail: formData.email,
      contactPersonPassword: formData.password,
      otp: "string" // Send as string as specified
    };

    console.log('Sending signup request:', requestBody);

    // Call API endpoint
    this.apiRequestService.postRequest<any>('SSMS/send-start-trail-otp-request', requestBody)
      .subscribe({
        next: (response: HttpResponse<any>) => {
          this.isLoading = false;
          
          if (response.status === 200) {
            console.log('OTP sent successfully:', response.body);
            this.successMessage = response.body || `OTP sent to ${formData.email}`;
            
            // Store signup data in sessionStorage for OTP resend
            sessionStorage.setItem('signupData', JSON.stringify(requestBody));
            
            // Navigate to verify OTP page after 2 seconds
            setTimeout(() => {
              this.router.navigate(['/auth/verify-otp'], {
                queryParams: { email: formData.email }
              });
            }, 2000);
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error sending OTP:', error);
          this.errorMessage = error.error?.message || 'Failed to send OTP. Please try again.';
        }
      });
  }

  get f() {
    return this.signupForm.controls;
  }

  goToSignIn(): void {
    this.router.navigate(['/auth/sign-in']);
  }

  goToHome(): void {
    window.location.href = 'http://shiftclockai.com/';
  }

  goToSignUp(): void {
    this.currentStep = 1;
  }
}
